import type * as React from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import * as api from '@gitbook/api-client';
import { ORGANIZATION_TITLE_MAX_LENGTH, ORGANIZATION_TITLE_MIN_LENGTH } from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import { getWorkDomainFromEmail } from '@gitbook/app-database';
import { Icon } from '@gitbook/spine-icons';
import {
    BooleanField,
    Button,
    Checkbox,
    type CheckboxProps,
    FieldError,
    Form,
    Label,
    TextField,
    TextInput,
    toast,
    useFieldProps,
} from '@gitbook/spine-react';

import { useSetAnalyticsScreen } from '../../navigation';
import {
    apiClient,
    clearOnboardingParam,
    getOnboardingParam,
    OnboardingParamName,
    useLoggedInUser,
} from '../../state';
import { getOrgInitialTitle } from './utils';
import { WelcomeLayout } from './WelcomeLayout';

type CreateOrganizationFormInputs = {
    title: string;
    emailDomainEnabled: boolean;
};

/**
 * Screen to create an organization.
 * If `onClickCancel` is not passed, the user is "trapped" and has to create an organization.
 * (This is what we want after the user has just signed up and isn't a member of any organizations yet).
 */
export function CreateOrganizationScreen(props: {
    reason?: analytics.OrganizationUpgradeReason;
    isClosable?: boolean;
    onClickCancel?: () => void;

    onStartCreating?: () => void;

    // flow controls
    onNext: ({ organization }: { organization: api.Organization }) => Promise<void>;
}): React.ReactElement {
    const { onClickCancel, reason, isClosable = true, onNext, onStartCreating } = props;

    const user = useLoggedInUser();
    const emailDomain = user.email ? getWorkDomainFromEmail(user.email) : null;

    const form = useForm<CreateOrganizationFormInputs>({
        defaultValues: {
            title:
                getOnboardingParam(OnboardingParamName.ORG_NAME) ??
                getOrgInitialTitle({ email: user.email }),
            emailDomainEnabled: !!emailDomain,
        },
    });

    const { formState } = form;

    useSetAnalyticsScreen(
        {
            screen: 'welcome_create',
            reason,
        },
        []
    );

    /** Create the organization with an ultimate site and go to the next step */
    const onSubmit: SubmitHandler<CreateOrganizationFormInputs> = async (data) => {
        onStartCreating?.();

        await toast.catch(async () => {
            const { data: organization } = await apiClient.organizations.createOrganization({
                title: data.title,
                type: api.OrganizationType.Business,
                emailDomains: emailDomain && data.emailDomainEnabled ? [emailDomain] : [],
            });

            clearOnboardingParam(OnboardingParamName.ORG_NAME);

            await onNext({ organization });
        }, 'Error while creating organization');
    };

    const title = form.watch('title');

    return (
        <WelcomeLayout title={title}>
            {isClosable || onClickCancel ? (
                <div className="top-4 left-4 self-start justify-self-start lg:absolute">
                    <Button
                        label="Back"
                        kind="blank"
                        leadingIcon={Icon.ArrowLeft}
                        href={isClosable ? { screen: { type: 'home' } } : undefined}
                        onPress={onClickCancel}
                        disabled={formState.isSubmitting}
                    />
                </div>
            ) : null}

            <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
                <div className="flex w-full flex-col gap-4">
                    <div className="heading-page">
                        <p className="text-base/60">Set up your organization.</p>
                        <p>Your docs live here.</p>
                    </div>
                </div>

                <div className="flex w-full flex-col gap-3">
                    <TextField
                        name="title"
                        required
                        minLength={ORGANIZATION_TITLE_MIN_LENGTH}
                        maxLength={ORGANIZATION_TITLE_MAX_LENGTH}
                        validate={(value) => {
                            if (
                                value.length < ORGANIZATION_TITLE_MIN_LENGTH ||
                                value.length > ORGANIZATION_TITLE_MAX_LENGTH
                            ) {
                                return 'Enter a name between 2 and 300 characters';
                            }
                        }}
                    >
                        <Label>Organization name</Label>
                        <TextInput
                            placeholder="e.g. MyCompany"
                            readOnly={formState.isSubmitting}
                            autoFocus
                        />
                        <FieldError />
                    </TextField>

                    {emailDomain ? (
                        <BooleanField name="emailDomainEnabled">
                            <Label
                                headless
                                className="flex items-center gap-2 text-muted text-ui-base"
                            >
                                <CheckboxWithFieldProps
                                    size="small"
                                    readOnly={formState.isSubmitting}
                                />
                                <span>
                                    Let teammates join this organization with their{' '}
                                    <strong>@{emailDomain}</strong> emails
                                </span>
                            </Label>
                        </BooleanField>
                    ) : null}
                </div>

                <div className="flex w-full">
                    <Button
                        type="submit"
                        kind="primary"
                        disabled={!title}
                        loadable={[formState.isSubmitting]}
                        label="Create organization"
                        size="large"
                        className="w-full"
                    />
                </div>
            </Form>
        </WelcomeLayout>
    );
}

/**
 * Checkbox component that properly connects to form fields using useFieldProps
 */
function CheckboxWithFieldProps(props: CheckboxProps) {
    const fieldProps = useFieldProps({});
    return (
        <Checkbox
            {...props}
            value={fieldProps.value}
            onChange={fieldProps.onChange}
            disabled={fieldProps.disabled}
        />
    );
}
